import {DateTime} from "luxon";
import {NgbDate, NgbTimeStruct} from "@ng-bootstrap/ng-bootstrap";
import {DateTimeUnit} from "luxon/src/datetime";

export class DateHelpers {
  static minDate: Date = new Date(-8640000000000000);
  static maxDate: Date = new Date(8640000000000000);

  static sortDates(from: Date, to: Date) {
    const from1 = from != null ? from.getTime() : Number.MAX_SAFE_INTEGER;
    const to1 = to != null ? to.getTime() : Number.MAX_SAFE_INTEGER;
    return from1 - to1;
  }

  static equals(from?: Date, to?: Date) {
    const defaultDate = new Date(1, 1, 1);
    return (from || defaultDate).toISOString() === (to || defaultDate).toISOString();
  }

  static equalsDate(from?: Date, to?: Date) {
    const defaultDate = new Date(1, 1, 1);
    return (from || defaultDate).toDateString() === (to || defaultDate).toDateString();
  }

  static format(date: Date, format: string) {
    return DateTime.fromJSDate(date).toFormat(format);
  }

  static formatDate(date: Date, format: string = 'yyyy-MM-dd', utc: boolean = false) {
    if (utc) {
      return DateTime.utc(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()).toFormat(format);
    }
    return DateTime.fromJSDate(date).toFormat(format);
  }

  static formatDateTime(date?: Date, format: string = "yyyy-MM-dd'T'HH:mm", utc: boolean = false) {
    if(!date){
      return DateTime.invalid('Date not specified').toString()
    }
    if (utc) {
      return DateTime.utc(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()).toFormat(format);
    }
    try {
      return DateTime.fromJSDate(date).toFormat(format);
    } catch (e) {
      return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
    }
  }

  static toIsoString(date: Date) {
    return DateTime.fromJSDate(date).startOf('second').toISO({suppressMilliseconds: true});
  }

  static parseDate(dateString: string) {
    return DateTime.fromISO(dateString).plus({hours: 12}).toJSDate();
  }

  static parseDateTime(dateString: string, format: string = "yyyy-MM-dd'T'HH:mm") {
    if(format){
      return DateTime.fromFormat(dateString, format).toJSDate();
    }
    return DateTime.fromISO(dateString).plus({hours: 12}).toJSDate();
  }

  static endOf(date: Date, unitOfTime: DateTimeUnit) {
    return DateTime.fromJSDate(date).endOf(unitOfTime).toJSDate();
  }

  static startOf(date: Date, unitOfTime: DateTimeUnit) {
    return DateTime.fromJSDate(date).startOf(unitOfTime).toJSDate();
  }

  static getTime<T>(o: T, propertyGet: (obj: T) => Date): string {
    const date = propertyGet(o);
    if (date && !(date.getHours() === 0 && date.getMinutes() === 0)) {
      return DateHelpers.formatDate(date, 'HH:mm');
    }
    return '';
  }

  static setTime<T>(o: T, propertyGet: (obj: T) => Date, time: string = '00:00') {
    if (time === '') {
      return null
    }
    const timeM = DateTime.fromFormat(time, 'H:mm');
    if (timeM.isValid) {
      const copiedDate = DateTime.fromJSDate(propertyGet(o)).set({hour: timeM.hour, minute: timeM.minute});
      return copiedDate.toJSDate();
    }
    return propertyGet(o);
  }

  static setDate<T>(o: T, propertyGet: (obj: T) => Date, from?: Date, resetHours: boolean = false) {
    let dateTime = DateTime.fromJSDate(propertyGet(o) ?? from ?? new Date());
    if (from) {
      const fromM = DateTime.fromJSDate(from);
      dateTime = dateTime.set({year: fromM.year, month: fromM.month, day: fromM.day});
      if (resetHours) {
        dateTime = dateTime.set({hour: 0, minute: 0, second: 0});
      }
    }
    return dateTime.toJSDate();
  }

  static isOneMonth(from: DateTime, to: DateTime): boolean {
    return from.day === 1 && to.day === to.daysInMonth && from.month === to.month;
  }

  static getNgbDate(jsDate: Date): NgbDate {
    return new NgbDate(jsDate.getFullYear(), jsDate.getMonth() + 1, jsDate.getDate());
  }

  static isToday(date: Date) {
    const today = new Date();
    return date?.getDate() === today.getDate() && date?.getMonth() === today.getMonth() && date?.getFullYear() === today.getFullYear();
  }

  static getNgbTimeStruct(date: Date): NgbTimeStruct {
    return {hour: date?.getHours() ?? 0, minute: date?.getMinutes() ?? 0, second: date?.getSeconds() ?? 0};
  }
}

